export const REFERRAL_ATTRIBUTION_STATUS = {
  CLICKED: "clicked",
  SIGNED_UP: "signed_up",
  ELIGIBLE: "eligible",
  EXPIRED: "expired",
  BLOCKED: "blocked",
} as const;

export const REFERRAL_BLOCKED_REASON = {
  SELF_REFERRAL: "self_referral",
  DUPLICATE_ATTRIBUTION: "duplicate_attribution",
  SAME_PAYOUT_ACCOUNT: "same_payout_account",
  INVALID_INVITE: "invalid_invite",
} as const;

export const REFERRAL_REWARD_TYPE = {
  NETWORK_COMMISSION: "network_commission",
  TIER_BOOST: "tier_boost",
  REFEREE_FIRST_MONTH_BONUS: "referee_first_month_bonus",
} as const;

export const REFERRAL_REWARD_STATUS = {
  ESTIMATED: "estimated",
  PENDING: "pending",
  APPROVED: "approved",
  VOID: "void",
} as const;

export type ReferralAttributionStatus =
  (typeof REFERRAL_ATTRIBUTION_STATUS)[keyof typeof REFERRAL_ATTRIBUTION_STATUS];

export type ReferralBlockedReason =
  (typeof REFERRAL_BLOCKED_REASON)[keyof typeof REFERRAL_BLOCKED_REASON];

export type ReferralRewardType =
  (typeof REFERRAL_REWARD_TYPE)[keyof typeof REFERRAL_REWARD_TYPE];

export type ReferralRewardStatus =
  (typeof REFERRAL_REWARD_STATUS)[keyof typeof REFERRAL_REWARD_STATUS];

export interface ReferralPublicCreator {
  id: string;
  displayName: string;
  username: string;
  avatarUrl?: string;
  nicheTags: string[];
  nicheBlurb: string;
  inviteUrl: string;
  lifetimeClicks: number;
  lifetimeApplications: number;
}

export interface ReferralDashboardSummary {
  inviteCode: string;
  inviteUrl: string;
  shareMessage: string;
  commissionsEarned: number;
  tierBoostEarned: number;
  refereeBonusEarned: number;
  leads: number;
  conversions: number;
  eligibleReferralCount: number;
  currentTier?: "tier_1" | "tier_2";
  currentTierBoostRate: number;
  nextTierTarget?: number;
}

export interface ReferralListItem {
  id: string;
  creatorName: string;
  creatorUsername?: string;
  creatorAvatarUrl?: string;
  signupStatus: "pending" | "signed_up";
  firstJobShareStatus: "pending" | "shared";
  firstEarningMonth?: string;
  referredCreatorTotalEarnings: number;
  monthlyNetworkCommission: number;
  totalNetworkCommission: number;
  earningWindowStatus: "not_started" | "active" | "ended";
  signupAt?: string;
  firstJobShareAt?: string;
}

export interface ReferralRewardEntry {
  id: string;
  month: string;
  type: ReferralRewardType;
  baseRevenue: number;
  rate: number;
  amount: number;
  status: ReferralRewardStatus;
  referredCreatorName?: string;
  createdAt?: string;
}

export interface ReferralResourceLink {
  title: string;
  description: string;
  href: string;
}

export interface ReferralLeaderboardEntry {
  rank: number;
  creatorId: string;
  name: string;
  username?: string;
  avatarUrl?: string;
  referrals: number;
  isAnonymous?: boolean;
}

export interface ReferralLeaderboard {
  topReferrers: ReferralLeaderboardEntry[];
  currentUser: ReferralLeaderboardEntry;
}

export interface ReferralDashboard {
  summary: ReferralDashboardSummary;
  referrals: ReferralListItem[];
  resources: ReferralResourceLink[];
  payouts: ReferralRewardEntry[];
  leaderboard: ReferralLeaderboard;
}
